import { ApplicationController } from "../../support/application-controller";

function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
}

function parseErrorString(errorStr) {
    if (typeof errorStr !== 'string') {
        if (errorStr && typeof errorStr === 'object') return errorStr;
        return { message: String(errorStr || 'Unknown error') };
    }
    var match = errorStr.match(/HTTP status code:\s*(\d+)[\s\S]*?response body:\s*([\s\S]+)/);
    if (match) {
        var status = parseInt(match[1]);
        try {
            var body = JSON.parse(match[2]);
            return { status: status, type: body.category || body.status, message: body.message || errorStr, raw: errorStr };
        } catch(e) {}
    }
    return { message: errorStr };
}

function renderError(errorStr) {
    var parsed = parseErrorString(errorStr);
    var summary = '';
    if (parsed.status) summary += parsed.status + ' ';
    if (parsed.type) summary += parsed.type + ': ';
    summary += parsed.message || 'Unknown error';

    var html = '<span class="hubspot-tools__error-summary">' + escapeHtml(summary) + '</span>';

    if (parsed.raw && parsed.raw !== summary) {
        html += ' <button class="hubspot-tools__error-toggle">Show details</button>';
        html += '<pre class="hubspot-tools__error-details" style="display:none;">' + escapeHtml(parsed.raw) + '</pre>';
    }
    return html;
}

function statusBadge(status) {
    var cssStatus = String(status || '').replace(/_/g, '-');
    return '<span class="hubspot-tools__status-badge hubspot-tools__status-badge--' + escapeHtml(cssStatus) + '">' + escapeHtml(status) + '</span>';
}

function renderFilterChips(statusCounts) {
    var total = Object.values(statusCounts).reduce(function(a, b) { return a + b; }, 0);
    var html = '<div class="hubspot-tools__filter-chips">';
    html += '<button type="button" class="hubspot-tools__filter-chip hubspot-tools__filter-chip--active" data-filter="all">All (' + total + ')</button>';
    var order = ['created', 'would_create', 'skipped', 'error'];
    order.forEach(function(status) {
        if (statusCounts[status]) {
            html += '<button type="button" class="hubspot-tools__filter-chip" data-filter="' + escapeHtml(status) + '">' + escapeHtml(status.replace(/_/g, ' ')) + ' (' + statusCounts[status] + ')</button>';
        }
    });
    Object.keys(statusCounts).forEach(function(status) {
        if (order.indexOf(status) === -1) {
            html += '<button type="button" class="hubspot-tools__filter-chip" data-filter="' + escapeHtml(status) + '">' + escapeHtml(status.replace(/_/g, ' ')) + ' (' + statusCounts[status] + ')</button>';
        }
    });
    html += '</div>';
    return html;
}

function bindFilterChips(container) {
    var chips = container.querySelectorAll('.hubspot-tools__filter-chip');
    var table = container.querySelector('.hubspot-tools__filterable-table');
    if (!chips.length || !table) return;

    chips.forEach(function(chip) {
        chip.addEventListener('click', function() {
            chips.forEach(function(c) { c.classList.remove('hubspot-tools__filter-chip--active'); });
            chip.classList.add('hubspot-tools__filter-chip--active');
            var filter = chip.getAttribute('data-filter');
            var rows = table.querySelectorAll('tbody tr');
            rows.forEach(function(row) {
                if (filter === 'all' || row.getAttribute('data-status') === filter) {
                    row.style.display = '';
                } else {
                    row.style.display = 'none';
                }
            });
        });
    });
}

/**
 * Stimulus controller for HubSpot seed data operations.
 * Handles create/list/cleanup with progress polling.
 */
export default class extends ApplicationController {
    static targets = [
        "emailInput",
        "dryRunCheckbox",
        "createButton",
        "cleanupButton",
        "listButton",
        "statusMessage",
        "progressPanel",
        "listPanel",
        "listContent",
        "resultsPanel",
        "resultsContent"
    ];

    connect() {
        this.polling = false;
        this.listVisible = false;
        this.checkInitialStatus();
        if (this.hasResultsContentTarget) {
            this.resultsContentTarget.addEventListener('click', function(e) {
                if (e.target.classList.contains('hubspot-tools__error-toggle')) {
                    var details = e.target.nextElementSibling;
                    if (details) {
                        var isHidden = details.style.display === 'none';
                        details.style.display = isHidden ? 'block' : 'none';
                        e.target.textContent = isHidden ? 'Hide details' : 'Show details';
                    }
                }
            });
        }
    }

    disconnect() {
        this.stopPolling();
    }

    // Actions

    createSeed(event) {
        if (event) event.preventDefault();

        var email = this.hasEmailInputTarget ? this.emailInputTarget.value : "";
        if (!email) {
            this.showMessage("Email is required.");
            return;
        }

        var dryRun = this.hasDryRunCheckboxTarget ? this.dryRunCheckboxTarget.checked : true;

        if (!dryRun && !confirm("This will create test data in HubSpot and the local database. Continue?")) {
            return;
        }

        this.disableButtons();
        this.showMessage("Enqueuing seed create job...");

        fetch("/admin/hubspot/seed/create", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify({ email: email, dry_run: dryRun })
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    this.startPolling();
                } else {
                    this.enableButtons();
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    listSeed(event) {
        if (event) event.preventDefault();

        if (this.listVisible) {
            // Hide the panel
            if (this.hasListPanelTarget) this.listPanelTarget.style.display = "none";
            this.listVisible = false;
            if (this.hasListButtonTarget) this.listButtonTarget.textContent = "List Existing";
            this.showMessage("");
            return;
        }

        this.showMessage("Loading seed data...");

        fetch("/admin/hubspot/seed/list", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    this.renderList(data);
                    this.listVisible = true;
                    if (this.hasListButtonTarget) this.listButtonTarget.textContent = "Hide Existing";
                } else {
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    cleanupSeed(event) {
        if (event) event.preventDefault();

        var dryRun = this.hasDryRunCheckboxTarget ? this.dryRunCheckboxTarget.checked : true;

        if (!dryRun && !confirm("This will DELETE all seed test data from HubSpot and the local database. Are you sure?")) {
            return;
        }

        this.disableButtons();
        this.showMessage("Enqueuing seed cleanup job...");

        fetch("/admin/hubspot/seed/cleanup", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify({ dry_run: dryRun })
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    this.startPolling();
                } else {
                    this.enableButtons();
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    // Private

    checkInitialStatus() {
        fetch("/admin/hubspot/seed/status", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.status === "running") {
                    this.disableButtons();
                    this.showMessage(data.message || "Job running...");
                    this.startPolling();
                } else if (data.status === "completed" || data.status === "failed") {
                    this.showMessage(data.message || data.status);
                    if (data.results) this.renderResults(data.results);
                }
            }.bind(this))
            .catch(function() {});
    }

    startPolling() {
        if (this.polling) return;
        this.polling = true;
        this.pollInterval = setInterval(function() { this.poll(); }.bind(this), 3000);
    }

    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.polling = false;
    }

    poll() {
        fetch("/admin/hubspot/seed/status", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                this.showMessage(data.message || "");
                if (data.status !== "running") {
                    this.stopPolling();
                    this.enableButtons();
                    if (data.results) this.renderResults(data.results);
                }
            }.bind(this))
            .catch(function(err) {
                console.error("Poll failed:", err);
            });
    }

    renderList(data) {
        if (!this.hasListPanelTarget || !this.hasListContentTarget) return;

        this.listPanelTarget.style.display = "block";

        if (data.total_users === 0 && data.total_organizations === 0) {
            this.listContentTarget.innerHTML = '<p>No seed test data found.</p>';
            this.showMessage("No seed data found.");
            return;
        }

        var html = '';

        if (data.users && data.users.length > 0) {
            html += '<h4>Seed Users (' + data.users.length + ')</h4>';
            html += '<table class="hubspot-tools__results-table"><thead><tr><th>ID</th><th>Email</th><th>Mappings</th></tr></thead><tbody>';
            data.users.forEach(function(u) {
                var mappings = u.mappings.map(function(m) { return escapeHtml(m.type) + ": " + escapeHtml(m.external_id); }).join(", ");
                html += '<tr><td>#' + escapeHtml(String(u.id)) + '</td><td>' + escapeHtml(u.email) + '</td><td>' + mappings + '</td></tr>';
            });
            html += '</tbody></table>';
        }

        if (data.organizations && data.organizations.length > 0) {
            html += '<h4>Seed Organizations (' + data.organizations.length + ')</h4>';
            html += '<table class="hubspot-tools__results-table"><thead><tr><th>ID</th><th>Name</th><th>Mappings</th><th>Subscriptions</th></tr></thead><tbody>';
            data.organizations.forEach(function(o) {
                var mappings = o.mappings.map(function(m) { return escapeHtml(m.type) + ": " + escapeHtml(m.external_id); }).join(", ");
                var subs = o.subscriptions.map(function(s) { return "#" + escapeHtml(String(s.id)) + " (" + escapeHtml(s.type) + ")"; }).join(", ");
                html += '<tr><td>#' + escapeHtml(String(o.id)) + '</td><td>' + escapeHtml(o.name) + '</td><td>' + mappings + '</td><td>' + subs + '</td></tr>';
            });
            html += '</tbody></table>';
        }

        this.listContentTarget.innerHTML = html;
        this.showMessage("Seed data loaded.");
    }

    renderResults(results) {
        if (!this.hasResultsPanelTarget || !this.hasResultsContentTarget) return;

        this.resultsPanelTarget.style.display = "block";
        var html = '<div class="hubspot-tools__results">';

        if (results.dry_run !== undefined) {
            html += '<p><strong>Mode:</strong> ' + escapeHtml(results.dry_run ? 'Dry run' : 'Live') + '</p>';
        }

        if (results.scenarios) {
            html += '<p>Total scenarios: ' + escapeHtml(String(results.scenarios.length));
            if (results.created_count !== undefined) html += ' | Success: ' + escapeHtml(String(results.created_count));
            if (results.error_count !== undefined) html += ' | Errors: ' + escapeHtml(String(results.error_count));
            html += '</p>';

            // Collect unique statuses for filter chips
            var statusCounts = {};
            results.scenarios.forEach(function(s) {
                var st = s.status || 'unknown';
                statusCounts[st] = (statusCounts[st] || 0) + 1;
            });

            html += renderFilterChips(statusCounts);

            html += '<table class="hubspot-tools__results-table hubspot-tools__filterable-table"><thead><tr><th>Scenario</th><th>Status</th><th>Details</th></tr></thead><tbody>';
            results.scenarios.forEach(function(s) {
                var details = '';
                if (s.error) {
                    details = renderError(s.error);
                }
                html += '<tr data-status="' + escapeHtml(s.status || 'unknown') + '"><td>' + escapeHtml(s.name || 'Unknown') + '</td><td>' + statusBadge(s.status) + '</td><td>' + details + '</td></tr>';
            });
            html += '</tbody></table>';
        }

        if (results.would_delete) {
            html += '<h4>Would Delete</h4>';
            html += '<p>HubSpot: ' + escapeHtml(String(results.would_delete.hubspot.contacts)) + ' contacts, ' +
                escapeHtml(String(results.would_delete.hubspot.companies)) + ' companies, ' +
                escapeHtml(String(results.would_delete.hubspot.deals)) + ' deals, ' +
                escapeHtml(String(results.would_delete.hubspot.subscriptions)) + ' subscriptions</p>';
            html += '<p>Local: ' + escapeHtml(String(results.would_delete.local.users)) + ' users, ' +
                escapeHtml(String(results.would_delete.local.organizations)) + ' organizations</p>';
        }

        if (results.deleted) {
            html += '<h4>Deleted</h4>';
            var hs = results.deleted.hubspot;
            if (hs) {
                html += '<p>HubSpot: ' + escapeHtml(String(hs.contacts || 0)) + ' contacts, ' +
                    escapeHtml(String(hs.companies || 0)) + ' companies, ' +
                    escapeHtml(String(hs.deals || 0)) + ' deals, ' +
                    escapeHtml(String(hs.subscriptions || 0)) + ' subscriptions</p>';
            }
            var local = results.deleted.local;
            if (local) {
                html += '<p>Local: ' + escapeHtml(String(local.users || 0)) + ' users, ' +
                    escapeHtml(String(local.organizations || 0)) + ' organizations</p>';
            }
        }

        html += '</div>';
        this.resultsContentTarget.innerHTML = html;
        bindFilterChips(this.resultsContentTarget);
    }

    showMessage(msg) {
        if (this.hasStatusMessageTarget) this.statusMessageTarget.textContent = msg;
    }

    disableButtons() {
        if (this.hasCreateButtonTarget) this.createButtonTarget.disabled = true;
        if (this.hasCleanupButtonTarget) this.cleanupButtonTarget.disabled = true;
    }

    enableButtons() {
        if (this.hasCreateButtonTarget) this.createButtonTarget.disabled = false;
        if (this.hasCleanupButtonTarget) this.cleanupButtonTarget.disabled = false;
    }

    getCSRFToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }
}
